/**
 * futureDateOfService — GDN-2784 sign-time guard client helpers.
 *
 * The backend blocks finalizing a note whose encounter has a materially
 * future date of service with a confirmable 409 (error_code
 * "future_date_of_service"). These pure helpers recognize that response and
 * build the official-time repair patch for the "Update date of service"
 * path: the honest correction is stamping the ACTUAL recording moment as the
 * official time, never just rewriting the billing date (the compliance
 * record's service date follows the encounter clock).
 */

import {
  buildInRoomPatch,
  type OfficialTimeEncounterLike,
  type OfficialTimePatch,
  type RecordingWindow,
  todayLocalDate,
} from '@features/encounters/components/OfficialTimeSelector/officialTimeModel';

export interface FutureDateOfServiceConflict {
  /** The encounter's effective date of service, ISO "YYYY-MM-DD". */
  dateOfService: string;
  /** Calendar days between the author-local today and the date. */
  daysInFuture: number;
}

/**
 * Recognize the guard's 409. Must be checked BEFORE any status-only 409
 * handling (the already-co-signed conflict matches on status alone).
 */
export const getFutureDateOfServiceConflict = (
  error: unknown,
): FutureDateOfServiceConflict | null => {
  if (typeof error !== 'object' || error === null) return null;
  const { status, data } = error as { status?: unknown; data?: unknown };
  if (status !== 409 || typeof data !== 'object' || data === null) return null;
  const body = data as {
    error_code?: unknown;
    date_of_service?: unknown;
    days_in_future?: unknown;
  };
  if (body.error_code !== 'future_date_of_service') return null;
  if (typeof body.date_of_service !== 'string') return null;
  return {
    dateOfService: body.date_of_service,
    daysInFuture: typeof body.days_in_future === 'number' ? body.days_in_future : 0,
  };
};

/**
 * The official-time patch for "Update date of service & sign": the in-room
 * (actual recording) times, including the billing date. Returns null when no
 * honest repair exists — no recoverable recording moment, or one that does
 * not land on the user's local today (repairing to any other day would trade
 * one mis-dated record for another; the dialog must fall back to
 * sign-anyway / manual adjustment).
 */
export const buildOfficialTimeRepairPatch = (
  encounter: OfficialTimeEncounterLike,
  recordingWindow: RecordingWindow | null,
): OfficialTimePatch | null => {
  const patch = buildInRoomPatch(encounter, {
    includeDateOfService: true,
    recordingWindow,
  });
  if (!patch || patch.date_of_service !== todayLocalDate()) return null;
  return patch;
};
